import { useMemo } from "react";

import { MeetOpsQuery } from "@app/analytics/meetops/types";
import {
  getQueryKeyFromDateRanges,
  getRoundedValues,
  PartialParsedMetric,
} from "@app/analytics/utils";
import useMeetings from "@app/common/hooks/meetings/useMeetings";
import { CoachingMetricData } from "@app/common/types";
import { User, UserMeeting } from "@app/common/types";

export type SessionMap = Map<string, CoachingMetricData>;

const EMPTY_METRIC: PartialParsedMetric = { value: null, formattedValue: "-" };

const generateSessionsDataMap = (meetings: UserMeeting[]): SessionMap => {
  return meetings.reduce((sessionMap, meeting) => {
    const talkTimePercent = getRoundedValues(
      meeting.talkTimePercent ?? null,
      "%"
    );

    const combinedSession: CoachingMetricData = {
      endTime: meeting.endTime,
      isRecurring: Boolean(meeting.seriesId),
      participants: meeting.numParticipants,
      sessionId: meeting.id,
      startTime: meeting.startTime,
      talkTimePercent,
      thumbnailUrl: meeting.thumbnailUrl,
      title: meeting.title,
      // bias and charisma values are placeholders; they will be overriden
      // in the charisma/bias hook
      bias: EMPTY_METRIC,
      charisma: EMPTY_METRIC,
    };

    sessionMap.set(meeting.id, combinedSession);
    return sessionMap;
  }, new Map<string, CoachingMetricData>());
};

const useCoachingSessions = ({
  dateRanges,
  enabled,
  userId,
}: {
  dateRanges: MeetOpsQuery["dateRanges"];
  enabled: boolean;
  userId: User["id"];
}) => {
  const dateRangeQueryKey = getQueryKeyFromDateRanges(dateRanges);

  const { isError, isLoading, meetings, refetch } = useMeetings(
    { dateRanges, userId },
    {
      enabled,
      refetchInterval: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const sessions = useMemo(
    () => generateSessionsDataMap(meetings),
    [isLoading, dateRangeQueryKey]
  );

  return {
    isError,
    isLoading,
    sessions,
    refetch,
  };
};

export default useCoachingSessions;
